import { Link } from "react-router-dom";
import {
  ArrowRight,
  ShieldCheck,
  Sparkles,
  HandCoins,
  Briefcase,
  Building2,
  Scale,
  Users2,
  HeartHandshake,
  CheckCircle2,
  Quote,
  Compass,
} from "lucide-react";
import { PolicyHeader } from "@/components/PolicyHeader";
import { SITE_IMAGES } from "@/data/site-images";

const CORE_PRINCIPLES = [
  "Integrity",
  "Trust",
  "Transparency",
  "Innovation",
  "Professional Excellence",
  "Customer Success",
];

const FINANCIAL_SERVICES = [
  "Personal Finance Solutions",
  "Business Loans",
  "Project Finance",
  "Working Capital Assistance",
  "Machinery & Equipment Finance",
  "Property Finance",
  "Agriculture Finance",
  "Gold Loan Assistance",
];

const INVESTMENT_SERVICES = [
  "Investment Advisory",
  "Mutual Fund Distribution",
  "Wealth Creation Planning",
  "Long-Term Financial Planning",
  "Retirement Planning",
  "Portfolio Guidance",
  "Asset Allocation Strategies",
];

const CORPORATE_ADVISORY = [
  "Business Strategy Consulting",
  "Project Planning",
  "Corporate Financial Advisory",
  "Business Expansion Planning",
  "Company Formation",
  "Startup Consulting",
  "Partnership & Joint Venture Advisory",
];

const LEGAL_COMPLIANCE = [
  "GST Registration & Compliance",
  "Income Tax Services",
  "ROC Compliance",
  "Accounting & Bookkeeping",
  "Documentation Support",
  "Corporate Legal Advisory",
];

const PARTNER_NETWORK = [
  "Direct Selling Agents (DSA)",
  "Associate Partners",
  "Agency Partners",
  "Branch Partners",
  "Business Development Executives",
  "Strategic Corporate Partnerships",
];

const WHY_CHOOSE = [
  "Customer-Centric Approach",
  "Transparent Business Practices",
  "Professional Financial Advisory",
  "Customized Financial Solutions",
  "Technology-Driven Services",
  "Experienced Team of Professionals",
  "Ethical Business Standards",
  "Quick Processing",
  "Secure Documentation",
  "Long-Term Relationship Focus",
  "Integrated Financial Solutions Under One Roof",
  "Dedicated Customer Support",
];

const PHILOSOPHY_LINES = [
  ["Finance should create opportunities", "not barriers."],
  ["Investment should create wealth", "not confusion."],
  ["Business funding should accelerate growth", "not increase complexity."],
  ["Professional advice should build confidence", "not uncertainty."],
];

const COMMITMENT_LINES = [
  "Every customer deserves transparency.",
  "Every entrepreneur deserves opportunity.",
  "Every business deserves strategic support.",
  "Every investor deserves professional guidance.",
  "Every family deserves financial security.",
];

function Section({ eyebrow, title, children, testId }) {
  return (
    <section data-testid={testId} className="py-16 md:py-20 border-t border-white/5">
      <div className="max-w-6xl mx-auto px-5 md:px-8">
        {eyebrow && (
          <div className="text-[11px] uppercase tracking-[0.3em] text-[#D4AF37] mb-3">
            {eyebrow}
          </div>
        )}
        {title && (
          <h2 className="font-serif text-3xl md:text-4xl text-white max-w-3xl leading-tight mb-8">
            {title}
          </h2>
        )}
        {children}
      </div>
    </section>
  );
}

function ServiceBlock({ icon: Icon, title, items, testId }) {
  return (
    <div
      data-testid={testId}
      className="rounded-2xl border border-[#D4AF37]/15 bg-[#0A1F44] p-6"
    >
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 grid place-items-center rounded-lg bg-[#D4AF37]/10 border border-[#D4AF37]/25">
          <Icon size={18} className="text-[#F0D788]" />
        </div>
        <h3 className="font-serif text-lg text-white">{title}</h3>
      </div>
      <ul className="space-y-2">
        {items.map((it) => (
          <li key={it} className="flex items-start gap-2 text-sm text-white/75">
            <CheckCircle2 size={13} className="text-[#D4AF37] mt-1 shrink-0" />
            {it}
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function About() {
  return (
    <div className="min-h-screen bg-[#04102B] text-white">
      <PolicyHeader
        eyebrow="About Us"
        title={
          <>
            Building <em className="not-italic text-[#D4AF37]">Trust</em>. Creating{" "}
            <span className="font-script italic text-[#F0D788]">Wealth</span>. Empowering Growth.
          </>
        }
        lead="A trusted, professionally-managed integrated financial services organization committed to empowering individuals, entrepreneurs, businesses, farmers, professionals, startups, MSMEs and corporate enterprises through innovative financial solutions and strategic advisory services."
      />

      {/* Hero banner image */}
      <main id="main-content" tabIndex={-1}>
      <div className="max-w-6xl mx-auto px-5 md:px-8 -mt-6 md:-mt-10">
        <div
          data-testid="about-hero-image"
          className="relative rounded-3xl overflow-hidden border border-[#D4AF37]/20 shadow-[0_20px_60px_-20px_rgba(0,0,0,0.6)] min-h-[260px] md:min-h-[420px] flex items-end"
        >
          <img
            src={SITE_IMAGES.heroes[0]}
            alt="Sahyog Finance — Global Financial Ecosystem"
            loading="lazy"
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#04102B]/90 via-[#04102B]/35 to-transparent pointer-events-none" />
          <div className="relative pt-32 md:pt-48 pb-5 md:pb-8 px-5 md:px-10 max-w-xl">
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#F0D788] mb-2">
              Integrated Finance · Wealth · Advisory
            </div>
            <h1 className="font-display text-2xl sm:text-3xl md:text-4xl text-white leading-tight">
              Building <em className="not-italic text-[#D4AF37]">Trust</em>. Creating{" "}
              <span className="font-script italic text-[#F0D788]">Wealth</span>. Empowering Growth.
            </h1>
            <div className="mt-3 md:mt-4 font-serif text-lg md:text-2xl text-white/85 leading-snug">
              One trusted platform for every financial decision in your life.
            </div>
          </div>
        </div>
      </div>

      {/* Intro */}
      <Section testId="about-intro">
        <div className="grid lg:grid-cols-3 gap-8 items-start">
          <div className="lg:col-span-2 space-y-5 text-base text-white/80 leading-relaxed">
            <p>
              Welcome to{" "}
              <strong className="text-white">
                Sahyog Finance &amp; Noble Infinity Asset Management Private Limited
              </strong>{" "}
              — founded with a vision to make financial services more transparent, accessible,
              ethical, and customer-centric. We bring together a comprehensive range of financial
              products and professional services under one trusted platform.
            </p>
            <p>
              Finance is more than simply providing loans or investment opportunities. It is
              about creating possibilities, building businesses, protecting families, generating
              wealth, and contributing to long-term economic development.
            </p>
            <p>
              Every financial solution we design is carefully structured to meet the evolving
              needs of our customers while maintaining the highest standards of governance,
              regulatory compliance, and ethical business practices.
            </p>
          </div>
          <div className="rounded-2xl border border-[#D4AF37]/25 bg-[#0A1F44] p-6">
            <div className="text-[11px] uppercase tracking-[0.25em] text-[#D4AF37] mb-3">
              Our Core Principles
            </div>
            <div className="flex flex-wrap gap-2">
              {CORE_PRINCIPLES.map((p) => (
                <span
                  key={p}
                  className="px-3 py-1.5 rounded-full border border-[#D4AF37]/25 bg-[#D4AF37]/5 text-xs text-white"
                >
                  {p}
                </span>
              ))}
            </div>
          </div>
        </div>
      </Section>

      {/* Who we are */}
      <Section
        testId="about-who"
        eyebrow="Who We Are"
        title="A customer-first ecosystem, not just another financial company"
      >
        <div className="space-y-5 text-base text-white/80 leading-relaxed max-w-4xl">
          <p>
            Sahyog Finance &amp; Noble Infinity is an emerging financial services company dedicated
            to delivering integrated financial solutions through a customer-first approach. We
            simplify finance by combining modern technology, professional expertise, personalized
            advisory, and transparent business practices into one comprehensive ecosystem.
          </p>
          <p>
            We continuously invest in digital transformation, intelligent financial systems,
            customer education, professional development, and innovative service delivery — so
            every customer receives world-class financial guidance and long-term value.
          </p>
          <p>
            Every customer has unique financial goals. Therefore, we focus on{" "}
            <strong className="text-white">designing customized financial strategies</strong>{" "}
            rather than offering one-size-fits-all solutions.
          </p>
        </div>
      </Section>

      {/* What we do */}
      <Section testId="about-what" eyebrow="What We Do" title="One platform — every financial need">
        <div className="mb-8 grid md:grid-cols-2 gap-5 items-stretch">
          <img
            src={SITE_IMAGES.heroes[6]}
            alt="Investment growth visualization"
            loading="lazy"
            data-testid="about-what-image"
            className="rounded-2xl object-cover w-full h-48 md:h-full border border-[#D4AF37]/15"
          />
          <div className="rounded-2xl border border-[#D4AF37]/20 bg-gradient-to-br from-[#0A1F44] to-[#04102B] p-6 md:p-8 flex flex-col justify-center">
            <Sparkles size={22} className="text-[#F0D788] mb-3" />
            <p className="text-base md:text-lg text-white leading-relaxed">
              Five integrated pillars — <strong>Finance</strong>, <strong>Investment</strong>,{" "}
              <strong>Advisory</strong>, <strong>Legal</strong> and{" "}
              <strong>Business Development</strong> — all delivered under one trusted brand.
            </p>
            <div className="mt-4 text-xs text-white/55">
              Every product is built around customer goals, regulatory compliance and long-term value.
            </div>
          </div>
        </div>
        <div className="grid md:grid-cols-2 gap-5">
          <ServiceBlock
            testId="services-financial"
            icon={HandCoins}
            title="Financial Services"
            items={FINANCIAL_SERVICES}
          />
          <ServiceBlock
            testId="services-investment"
            icon={Sparkles}
            title="Investment & Wealth Management"
            items={INVESTMENT_SERVICES}
          />
          <ServiceBlock
            testId="services-corporate"
            icon={Briefcase}
            title="Corporate & Business Advisory"
            items={CORPORATE_ADVISORY}
          />
          <ServiceBlock
            testId="services-legal"
            icon={Scale}
            title="Legal & Compliance Services"
            items={LEGAL_COMPLIANCE}
          />
          <div className="md:col-span-2">
            <ServiceBlock
              testId="services-partners"
              icon={Users2}
              title="Business Development Network"
              items={PARTNER_NETWORK}
            />
          </div>
        </div>
      </Section>

      {/* Philosophy */}
      <Section testId="about-philosophy" eyebrow="Our Philosophy" title="Every financial decision has the power to transform lives">
        <div className="grid md:grid-cols-2 gap-3 max-w-4xl">
          {PHILOSOPHY_LINES.map(([head, tail], i) => (
            <div
              key={head}
              data-testid={`philosophy-line-${i}`}
              className="rounded-xl border border-[#D4AF37]/15 bg-[#0A1F44] px-5 py-4"
            >
              <div className="text-sm text-white">
                <span className="text-[#F0D788]">{head}</span>{" "}
                <span className="text-white/70">— {tail}</span>
              </div>
            </div>
          ))}
        </div>
      </Section>

      {/* Why choose us */}
      <Section testId="about-why" eyebrow="Why Choose Us" title="What sets Sahyog Finance & Noble Infinity apart">
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {WHY_CHOOSE.map((w, i) => (
            <div
              key={w}
              data-testid={`why-choose-${i}`}
              className="flex items-start gap-3 rounded-xl border border-white/10 bg-white/[0.02] px-4 py-3"
            >
              <ShieldCheck size={15} className="text-[#D4AF37] mt-0.5 shrink-0" />
              <div className="text-sm text-white/85">{w}</div>
            </div>
          ))}
        </div>
      </Section>

      {/* Commitment */}
      <Section testId="about-commitment" eyebrow="Our Commitment" title="Beyond providing financial products">
        <div className="rounded-2xl border border-[#D4AF37]/20 bg-gradient-to-br from-[#0A1F44] to-[#04102B] p-7 md:p-10">
          <p className="text-base md:text-lg text-white/85 leading-relaxed max-w-3xl">
            We are committed to becoming a lifelong financial partner for every customer by
            delivering intelligent financial solutions, professional guidance, ethical business
            practices, and exceptional customer experiences.
          </p>
          <div className="mt-7 space-y-2.5">
            {COMMITMENT_LINES.map((l, i) => (
              <div
                key={l}
                data-testid={`commitment-${i}`}
                className="flex items-center gap-3 text-sm md:text-base text-white"
              >
                <HeartHandshake size={15} className="text-[#F0D788] shrink-0" /> {l}
              </div>
            ))}
          </div>
          <div className="mt-7 text-sm text-white/65 italic">That commitment defines who we are.</div>
        </div>
      </Section>

      {/* Future vision */}
      <Section
        testId="about-future"
        eyebrow="Our Vision for the Future"
        title="To become one of India's most respected integrated financial services organizations"
      >
        <div className="mb-10 rounded-3xl overflow-hidden border border-[#D4AF37]/20">
          <img
            src={SITE_IMAGES.heroes[2]}
            alt="Future of finance — nationwide expansion"
            loading="lazy"
            data-testid="about-future-image"
            className="w-full h-[180px] md:h-[260px] object-cover"
          />
        </div>
        <div className="grid md:grid-cols-2 gap-8 items-start">
          <div className="space-y-4 text-base text-white/80 leading-relaxed">
            <p>
              As we continue to grow, our goal is to combine innovation, technology, trust, and
              professional excellence to build a nationwide presence, empower millions of
              customers, support thousands of businesses, create meaningful employment
              opportunities, and contribute significantly to India's economic development.
            </p>
            <p className="text-white font-medium">
              Together, we are building a stronger financial future — one customer, one business,
              and one opportunity at a time.
            </p>
            <div className="pt-4 flex flex-wrap gap-3">
              <Link
                to="/vision"
                data-testid="about-vision-cta"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full btn-outline-gold text-sm"
              >
                Read our Vision & Mission <ArrowRight size={14} />
              </Link>
              <Link
                to="/careers"
                data-testid="about-careers-cta"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full btn-gold text-sm font-semibold"
              >
                Partner With Us <ArrowRight size={14} />
              </Link>
            </div>
          </div>
          <div className="rounded-2xl border border-[#D4AF37]/25 bg-gradient-to-br from-[#0A1F44] via-[#0c2654] to-[#04102B] p-8 md:p-10">
            <Quote size={28} className="text-[#D4AF37] mb-4" />
            <div className="text-[11px] uppercase tracking-[0.3em] text-[#F0D788] mb-3">
              Our Promise
            </div>
            <div className="font-serif text-xl md:text-2xl leading-snug text-white">
              "Your Success Is Our Mission.
              <br />
              Your Growth Is Our Responsibility.
              <br />
              <span className="text-[#F0D788]">Your Trust Is Our Greatest Asset.</span>"
            </div>
          </div>
        </div>
      </Section>

      {/* Final CTA */}
      <section className="py-16 md:py-20 border-t border-white/5">
        <div className="max-w-4xl mx-auto px-5 md:px-8 text-center">
          <Compass size={32} className="mx-auto text-[#F0D788] mb-4" />
          <h2 className="font-serif text-3xl md:text-4xl text-white mb-4">
            Ready to start your financial journey?
          </h2>
          <p className="text-base text-white/70 mb-7 max-w-2xl mx-auto">
            Talk to our team and discover how Sahyog Finance &amp; Noble Infinity can become your
            trusted financial partner for life.
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            <Link
              to="/apply/loan"
              data-testid="about-apply-cta"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full btn-gold text-sm font-semibold"
            >
              Apply for a Loan <ArrowRight size={14} />
            </Link>
            <Link
              to="/customer-services"
              data-testid="about-contact-cta"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full btn-outline-gold text-sm"
            >
              Contact Customer Care
            </Link>
          </div>
        </div>
      </section>
      </main>
    </div>
  );
}
